/**
 * XER-admin-only panel on /downloads: publish a new installer release, and
 * manage the products that can be published to.
 *
 * Flow: pick file + metadata → POST the installer through the backend
 * (progress bar) → backend finalize (HEAD + server-side SHA256 + catalog row).
 * Rendering is gated by the client-side isXerAdmin heuristic; the backend
 * endpoints are the real gate (require_xer_admin).
 *
 * The product dropdown is fetched from `GET /api/software-releases/products`
 * rather than built from the bundled label map, which is what lets a product
 * created this morning be published to from a bundle built last month. The
 * bundled map survives only as a fallback label where no product row is
 * reachable.
 */
import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/errorHandler';
import {
  usePublishReleaseMutation,
  useSoftwareProductsQuery,
  useUpdateProductMutation,
} from './Downloads.queries';
import { NewProductDialog } from './NewProductDialog';
import { buildProductOptions, reconcileSelectedProduct } from './productCatalog';
import { formatBytes } from './formatBytes';
import type { SoftwareProductRow } from '../../types/downloads';

/** Who will be able to see this once published. Read from the product's own
 * audience rather than a hardcoded map, so it is right for products created
 * after this bundle was built — and it is the only place a publisher sees
 * which side of the audience gate they are on before pressing Publish. */
const audienceHint = (audience: SoftwareProductRow['audience']): string =>
  audience === 'xer_internal'
    ? 'XER staff only — customers never see this product on /downloads.'
    : 'Visible to every signed-in user.';

export const PublishReleasePanel: React.FC = () => {
  const [product, setProduct] = useState('');
  const [version, setVersion] = useState('');
  const [channel, setChannel] = useState('stable');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [newProductOpen, setNewProductOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const productsQuery = useSoftwareProductsQuery();
  const publishMutation = usePublishReleaseMutation();
  const updateProductMutation = useUpdateProductMutation();
  const busy = publishMutation.isPending;
  // Upload done (100%) but mutation still running = backend verify/hash phase.
  const verifying = busy && uploadPercent === 100;

  const products = useMemo(() => productsQuery.data ?? [], [productsQuery.data]);
  const options = useMemo(() => buildProductOptions(products), [products]);
  // Derived, not stored: the list arrives after this component mounts, and an
  // archived (or newly created) product must not leave the form holding a key
  // the dropdown no longer offers — MUI renders that as a blank select that
  // still submits the stale value.
  const selectedProduct = reconcileSelectedProduct(product, options);
  const selectedOption = options.find((o) => o.value === selectedProduct);

  const handlePublish = async () => {
    if (!file || !version.trim() || !selectedProduct) return;
    setUploadPercent(0);
    try {
      const release = await publishMutation.mutateAsync({
        input: {
          product: selectedProduct,
          version: version.trim(),
          file_name: file.name,
          size_bytes: file.size,
          channel,
          release_notes: releaseNotes.trim() || null,
        },
        file,
        onProgress: setUploadPercent,
      });
      toast.success(`Published ${selectedOption?.label ?? release.product} ${release.version}`);
      setVersion('');
      setReleaseNotes('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to publish release'));
    } finally {
      setUploadPercent(null);
    }
  };

  const handleToggleArchived = async (row: SoftwareProductRow) => {
    try {
      await updateProductMutation.mutateAsync({
        key: row.key,
        input: { archived: !row.archived },
      });
      toast.success(`${row.label} ${row.archived ? 'unarchived' : 'archived'}`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update product'));
    }
  };

  const handleRename = async (row: SoftwareProductRow) => {
    const next = window.prompt(`Display name for ${row.key}`, row.label);
    if (next === null || next.trim() === row.label) return;
    if (!next.trim()) {
      toast.error('A display name is required');
      return;
    }
    try {
      await updateProductMutation.mutateAsync({ key: row.key, input: { label: next.trim() } });
      toast.success('Product renamed');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to rename product'));
    }
  };

  return (
    <Card variant="outlined" data-testid="publish-release-panel" sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Publish a release
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The installer uploads through the backend to S3; the backend then verifies its size and
          computes the SHA256 before the release becomes visible. XER admins only.
        </Typography>

        {productsQuery.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load products — publishing is unavailable until they load.
          </Alert>
        )}
        {productsQuery.isSuccess && options.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }} data-testid="no-publishable-products">
            No products are available to publish to. Create one first.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          {productsQuery.isLoading ? (
            <Skeleton variant="rounded" width={220} height={40} />
          ) : (
            <TextField
              select
              size="small"
              label="Product"
              value={selectedProduct}
              onChange={(e) => setProduct(e.target.value)}
              sx={{ minWidth: 220 }}
              disabled={busy || options.length === 0}
              SelectProps={{ inputProps: { 'data-testid': 'publish-product-select' } }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {selectedOption && (
            <Typography
              variant="caption"
              color={selectedOption.audience === 'xer_internal' ? 'warning.main' : 'text.secondary'}
              sx={{ alignSelf: 'center' }}
              data-testid="publish-audience-hint"
            >
              {audienceHint(selectedOption.audience)}
            </Typography>
          )}
          <TextField
            size="small"
            label="Version"
            placeholder="e.g. 2.1.0"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            disabled={busy}
            inputProps={{ 'data-testid': 'publish-version-input' }}
          />
          <TextField
            select
            size="small"
            label="Channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            sx={{ minWidth: 120 }}
            disabled={busy}
          >
            <MenuItem value="stable">stable</MenuItem>
            <MenuItem value="beta">beta</MenuItem>
          </TextField>
        </Box>

        <TextField
          fullWidth
          size="small"
          label="Release notes (optional)"
          multiline
          minRows={2}
          value={releaseNotes}
          onChange={(e) => setReleaseNotes(e.target.value)}
          disabled={busy}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            disabled={busy}
          >
            {file ? 'Change file' : 'Choose installer'}
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".exe,.msi,.zip,.apk"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {file && (
            <Typography variant="body2" data-testid="publish-selected-file">
              {file.name} ({formatBytes(file.size)})
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => void handlePublish()}
            disabled={busy || !file || !version.trim() || !selectedProduct}
            data-testid="publish-release-button"
          >
            Publish
          </Button>
        </Box>

        {busy && uploadPercent !== null && (
          <Box>
            <LinearProgress
              variant={verifying ? 'indeterminate' : 'determinate'}
              value={uploadPercent}
            />
            <Typography variant="caption" color="text.secondary">
              {verifying
                ? 'Verifying upload and computing SHA256…'
                : `Uploading… ${uploadPercent}%`}
            </Typography>
          </Box>
        )}
        {publishMutation.isError && !busy && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {extractErrorMessage(publishMutation.error, 'Publishing failed')}
          </Alert>
        )}

        {/* ---- Products ------------------------------------------------- */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3, mb: 1 }}>
          <Typography variant="subtitle2">Products</Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setNewProductOpen(true)}
            data-testid="new-product-button"
          >
            New product…
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Archiving removes a product from the dropdown above. Its published releases stay
          downloadable — nothing already in the field breaks.
        </Typography>

        {productsQuery.isLoading && (
          <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 1, mt: 1 }} />
        )}
        <Stack spacing={0.5} sx={{ mt: 1 }} data-testid="product-admin-list">
          {products.map((row) => (
            <Box
              key={row.key}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              data-testid={`product-row-${row.key}`}
            >
              <Typography
                variant="body2"
                sx={{ textDecoration: row.archived ? 'line-through' : 'none' }}
              >
                {row.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.key}
              </Typography>
              {row.audience === 'xer_internal' && (
                <Tooltip title="XER staff only — customers never see this product">
                  <Chip size="small" variant="outlined" label="Internal" />
                </Tooltip>
              )}
              {row.archived && <Chip size="small" variant="outlined" label="Archived" />}
              <Box sx={{ flex: 1 }} />
              <Tooltip title="Rename">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => void handleRename(row)}
                    disabled={updateProductMutation.isPending}
                    aria-label={`Rename ${row.key}`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={row.archived ? 'Unarchive' : 'Archive'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => void handleToggleArchived(row)}
                    disabled={updateProductMutation.isPending}
                    aria-label={`${row.archived ? 'Unarchive' : 'Archive'} ${row.key}`}
                  >
                    {row.archived ? (
                      <UnarchiveIcon fontSize="small" />
                    ) : (
                      <ArchiveIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ))}
        </Stack>

        <NewProductDialog
          open={newProductOpen}
          onClose={() => setNewProductOpen(false)}
          onCreated={setProduct}
        />
      </CardContent>
    </Card>
  );
};
